import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

function CompetitionOrganizerTab({
  organizers,
  organizerID,
  onOrganizerChange,
  selectedCompetitionOrganizerUserIDs = [],
  onCompetitionOrganizerUserToggle,
}) {
  const selectedOrganizer = organizers.find((organizer) => String(organizer.organizerID) === String(organizerID)) || null;
  const hasSingleOrganizer = organizers.length === 1;
  const organizerUsers = Array.isArray(selectedOrganizer?.organizerUsers) ? selectedOrganizer.organizerUsers : [];

  return (
    <section className="rounded-[24px] border border-white/10 bg-white/5 p-4">
      <div className="border-b border-white/10 pb-4 text-left">
        <h2 className="text-xl font-bold text-white">Järjestäjä</h2>
        <p className="mt-2 text-sm leading-6 text-slate-300">
          Valitse kilpailulle järjestäjä ennen kuin siirryt perustietoihin tai maksu- ja palkintoasetuksiin.
        </p>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <FormControl variant="filled" fullWidth className="md:col-span-2">
          <InputLabel id="competition-organizer-label">Järjestäjä</InputLabel>
          <Select
            labelId="competition-organizer-label"
            value={organizerID}
            onChange={(event) => onOrganizerChange(event.target.value)}
            disabled={hasSingleOrganizer}
            sx={{ backgroundColor: 'white', borderRadius: '12px' }}
          >
            {!organizerID ? <MenuItem value="" disabled>Valitse järjestäjä</MenuItem> : null}
            {organizers.map((organizer) => (
              <MenuItem key={organizer.organizerID} value={String(organizer.organizerID)}>
                {organizer.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div className="md:col-span-2 grid gap-3 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-4 text-left text-sm text-slate-200 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Valittu järjestäjä</p>
            <p className="mt-2 font-semibold text-white">{selectedOrganizer?.name || 'Ei valittu'}</p>
            <p className="mt-2 text-slate-300">
              {hasSingleOrganizer
                ? 'Järjestäjä asetettiin automaattisesti, koska sinulla on oikeudet vain yhden järjestäjän kilpailuihin.'
                : 'Järjestäjän maksutiedot kopioidaan seuraavassa välilehdessä kilpailun oletusarvoiksi.'}
            </p>
          </div>
          <div className="space-y-1 text-sm">
            <p>Tilinumero: <span className="font-semibold text-white">{selectedOrganizer?.bankAccount || 'Ei asetettu'}</span></p>
            <p>Tilisiirron saaja: <span className="font-semibold text-white">{selectedOrganizer?.bankRecipientName || selectedOrganizer?.name || 'Ei asetettu'}</span></p>
            <p>Tilimaksu: <span className="font-semibold text-white">{selectedOrganizer?.enableBankPayment ? 'Käytössä' : 'Ei käytössä'}</span></p>
            <p>MobilePay: <span className="font-semibold text-white">{selectedOrganizer?.mobilepayNumber || 'Ei asetettu'}</span></p>
            <p>MobilePay maksu: <span className="font-semibold text-white">{selectedOrganizer?.enableMobilepayPayment ? 'Käytössä' : 'Ei käytössä'}</span></p>
            <p>Tilisiirron viesti: <span className="font-semibold text-white">{selectedOrganizer?.bankPaymentMessage || 'Ei asetettu'}</span></p>
            <p>Yhteinen viitenumero: <span className="font-semibold text-white">{selectedOrganizer?.bankPaymentBarcode || 'Ei asetettu'}</span></p>
            <p>MobilePay-viesti: <span className="font-semibold text-white">{selectedOrganizer?.mobilepayMessage || 'Ei asetettu'}</span></p>
            <p>Henkilökohtainen viite: <span className="font-semibold text-white">{selectedOrganizer?.usePersonalReferenceNumber ? 'Käytössä' : 'Ei käytössä'}</span></p>
          </div>
        </div>

        <div className="md:col-span-2 rounded-2xl border border-white/10 bg-slate-950/20 p-4 text-left">
          <div className="border-b border-white/10 pb-4">
            <h3 className="text-lg font-bold text-white">Kilpailun ylläpitäjät</h3>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              Valitse järjestäjän käyttäjistä tämän kilpailun ylläpitäjät. Kilpailun lisääjä valitaan automaattisesti, jos hän kuuluu järjestäjän käyttäjiin.
            </p>
            <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
              Valittuna {selectedCompetitionOrganizerUserIDs.length} ylläpitäjää
            </p>
          </div>

          {organizerUsers.length ? (
            <>
              {selectedCompetitionOrganizerUserIDs.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
                  Valitse vähintään yksi kilpailun ylläpitäjä ennen tallennusta.
                </div>
              ) : null}

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {organizerUsers.map((organizerUser) => {
                  const organizerUserID = Number(organizerUser?.organizerUserID ?? 0);
                  const isChecked = selectedCompetitionOrganizerUserIDs.includes(organizerUserID);

                  return (
                    <FormControlLabel
                      key={organizerUserID}
                      className="m-0 rounded-2xl border border-white/10 bg-white/5 px-3 py-3"
                      sx={{
                        alignItems: 'flex-start',
                        '.MuiCheckbox-root': {
                          marginTop: '-2px',
                        },
                        '.MuiFormControlLabel-label': {
                          width: '100%',
                        },
                      }}
                      control={(
                        <Checkbox
                          checked={isChecked}
                          onChange={() => onCompetitionOrganizerUserToggle?.(organizerUserID)}
                          sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }}
                        />
                      )}
                      label={(
                        <span className="block">
                          <span className="block text-sm font-semibold text-white">{organizerUser.name}</span>
                          <span className="mt-1 block text-xs text-slate-400">{organizerUser.email || 'Ei sähköpostia'}</span>
                          {Array.isArray(organizerUser.roleLabels) && organizerUser.roleLabels.length ? (
                            <span className="mt-2 block text-xs uppercase tracking-[0.18em] text-cyan-300">
                              {organizerUser.roleLabels.join(' · ')}
                            </span>
                          ) : null}
                          {organizerUser.isPending ? (
                            <span className="mt-2 block text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">
                              Kutsu odottaa rekisteröitymistä
                            </span>
                          ) : null}
                        </span>
                      )}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <div className="mt-4 rounded-2xl border border-dashed border-white/10 bg-white/5 px-4 py-6 text-center text-sm text-slate-400">
              Valitulla järjestäjällä ei ole vielä käyttäjiä, joista voisi valita kilpailun ylläpitäjiä.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default CompetitionOrganizerTab;